import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Loader, Text} from '@gravity-ui/uikit';
import {Crop, ReactCrop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import styles from './ImageUploadModal.module.scss';
import {useImageUpload, PreparedImageData} from '../../lib';
import {useParams} from 'react-router';
import {ActionModal} from '../../../../shared';
import {ImageType} from '../../model';

interface ImageUploadModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  file: File | null;
  previewUrl: string | null;
  onUploadSuccess: () => void;
  fundraiserId?: string;
  imageType?: ImageType;
  isCreating?: boolean;
  aspectRatio?: number; // Соотношение сторон (ширина / высота)
  onPrepareImageData?: (data: PreparedImageData, previewUrl: string) => void;
}

export const ImageUploadModal = ({
  open,
  setOpen,
  file,
  previewUrl: initialPreviewUrl,
  onUploadSuccess,
  fundraiserId: propFundraiserId,
  imageType = ImageType.MAIN,
  isCreating = false,
  aspectRatio = 16 / 9, // По умолчанию горизонтальное 16:9
  onPrepareImageData,
}: ImageUploadModalProps) => {
  const [crop, setCrop] = useState<Crop | null>(null);
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const [resizeWidth, setResizeWidth] = useState<number | null>(null);
  const [resizeHeight, setResizeHeight] = useState<number | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [originalWidth, setOriginalWidth] = useState<number | null>(null);
  const [originalHeight, setOriginalHeight] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialPreviewUrl);
  const params = useParams();

  const fundraiserId = propFundraiserId || (params.id as string);
  const {
    prepareUpload,
    uploadImage,
    prepareImageData,
    isPreparing,
    isUploading,
    error: uploadError,
  } = useImageUpload(isCreating ? undefined : fundraiserId, imageType, isCreating);

  // Сброс состояния при закрытии модального окна или смене файла
  useEffect(() => {
    if (!open) {
      setCrop(null);
      setCompletedCrop(null);
      setPreviewUrl(null);
      setOriginalWidth(null);
      setOriginalHeight(null);
      imgRef.current = null;
    } else {
      setPreviewUrl(initialPreviewUrl);
    }
  }, [open, initialPreviewUrl]);

  useEffect(() => {
    if (open && file && !isCreating) {
      prepareUpload(file);
    }
  }, [open, file, prepareUpload, isCreating]);

  useEffect(() => {
    if (open && file && !previewUrl) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    }
  }, [open, file, previewUrl]);

  // Обработчик загрузки изображения в ReactCrop
  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      imgRef.current = e.currentTarget;
      const {naturalWidth: width, naturalHeight: height} = e.currentTarget;
      setOriginalWidth(width);
      setOriginalHeight(height);

      const displayWidth = e.currentTarget.width;
      const displayHeight = e.currentTarget.height;

      let initialCropWidth, initialCropHeight;

      // Определяем, вертикальное ли соотношение (для историй)
      const isVerticalAspect = aspectRatio < 1;

      if (isVerticalAspect) {
        // Для вертикальных соотношений (экраны историй)
        initialCropWidth = Math.min(300, displayWidth * 0.8);
        initialCropHeight = initialCropWidth / aspectRatio;

        // Проверяем, не выходит ли за границы по высоте
        if (initialCropHeight > displayHeight * 0.9) {
          initialCropHeight = displayHeight * 0.9;
          initialCropWidth = initialCropHeight * aspectRatio;
        }
      } else {
        // Для горизонтальных соотношений (обычные изображения)
        if (displayWidth / displayHeight > aspectRatio) {
          initialCropHeight = Math.min(400, displayHeight * 0.8);
          initialCropWidth = initialCropHeight * aspectRatio;
        } else {
          initialCropWidth = Math.min(500, displayWidth * 0.8);
          initialCropHeight = initialCropWidth / aspectRatio;
        }
      }

      const initialCrop: Crop = {
        unit: 'px',
        width: initialCropWidth,
        height: initialCropHeight,
        x: (displayWidth - initialCropWidth) / 2,
        y: (displayHeight - initialCropHeight) / 2,
      };

      setCrop(initialCrop);
      setCompletedCrop(initialCrop);
    },
    [aspectRatio],
  );

  // Обновляем resizeWidth/Height при изменении completedCrop
  useEffect(() => {
    if (completedCrop && imgRef.current && originalWidth && originalHeight) {
      const scaleX = originalWidth / imgRef.current.width;
      const scaleY = originalHeight / imgRef.current.height;

      setResizeWidth(Math.round(completedCrop.width * scaleX));
      setResizeHeight(Math.round(completedCrop.height * scaleY));
    } else {
      setResizeWidth(null);
      setResizeHeight(null);
    }
  }, [completedCrop, originalWidth, originalHeight]);

  // Функция для получения превью из canvas
  const getCanvasPreview = (): string | null => {
    if (!canvasRef.current) return null;
    return canvasRef.current.toDataURL('image/jpeg', 0.95);
  };

  // Обработчик нажатия кнопки "Сохранить"
  const handleUpload = async () => {
    if (completedCrop && imgRef.current && canvasRef.current && resizeWidth && resizeHeight) {
      if (isCreating && onPrepareImageData) {
        // Режим создания нового объекта - готовим данные для формы
        const imageData = await prepareImageData(
          completedCrop,
          resizeWidth,
          resizeHeight,
          imgRef as React.RefObject<HTMLImageElement>,
          canvasRef as React.RefObject<HTMLCanvasElement>,
        );

        if (imageData) {
          const previewUrl = getCanvasPreview();
          if (previewUrl) {
            onPrepareImageData(imageData, previewUrl);
          } else {
            onPrepareImageData(imageData, '');
          }
          onUploadSuccess();
        }
      } else {
        // Режим обновления существующего объекта - загружаем сразу
        await uploadImage(
          completedCrop,
          resizeWidth,
          resizeHeight,
          imgRef as React.RefObject<HTMLImageElement>,
          canvasRef as React.RefObject<HTMLCanvasElement>,
        );

        if (!uploadError) {
          onUploadSuccess();
        } else {
          const errorMsg = uploadError || 'Не удалось загрузить изображение.';
          console.error('Upload error:', errorMsg);
        }
      }
    } else {
      console.error('Upload conditions not met:', {
        completedCrop,
        imgRef: imgRef.current,
        canvasRef: canvasRef.current,
        resizeWidth,
        resizeHeight,
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const isLoading = isPreparing || isUploading;

  return (
    <ActionModal
      open={open}
      size="m"
      onClose={handleClose}
      setOpen={setOpen}
      submitForm={handleUpload}
      caption="Превью"
      buttonsSize={'l'}
      textButtonApply="Сохранить"
      textButtonCancel="Отмена"
      loading={isLoading}>
      <div className={styles.modalContent}>
        {isLoading && (
          <div className={styles.loader}>
            <Loader />
            <Text variant="body-1">Загружаем изображение</Text>
          </div>
        )}
        {previewUrl && !isPreparing && (
          <div className={styles.cropContainer}>
            <ReactCrop
              crop={crop || undefined}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c: Crop) => setCompletedCrop(c)}
              aspect={aspectRatio}
              circularCrop={false}
              className={styles.cropper}
              disabled={isLoading}>
              <img
                ref={imgRef}
                src={previewUrl}
                onLoad={onImageLoad}
                alt="Предпросмотр"
                className={styles.previewImage}
                style={{opacity: isLoading ? 0.5 : 1}}
              />
            </ReactCrop>
          </div>
        )}

        {/* Скрытый canvas для отрисовки обрезанного изображения */}
        <canvas ref={canvasRef} className={styles.hiddenCanvas} />
      </div>
    </ActionModal>
  );
};
